package com.exura.pages.publicplans

import androidx.compose.runtime.*
import com.exura.components.buttons.AsyncButton
import com.exura.components.buttons.ButtonOutlineVariant
import com.exura.components.dialogs.DialogPanelLargeVariant
import com.exura.components.icons.InlineIcon
import com.exura.components.icons.UntiledIcon
import com.exura.components.layouts.NavigableLayoutScope
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.ExuraBaseWeightVariant
import com.exura.components.pageTypography.ExuraCommentaryTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.GeneralListEntry
import com.exura.components.pageTypography.HoverableListEntryVariant
import com.exura.components.pageTypography.PageErrorContent
import com.exura.components.pageTypography.PageMainContent
import com.exura.components.pageTypography.PageNavigationHeaderSection
import com.exura.components.pageTypography.PageOptionsSection
import com.exura.components.pageTypography.PageSecondaryContent
import com.exura.components.pageTypography.UnderwrittenText
import com.exura.features.localization.commonLocalization
import com.exura.features.plandefinition.planLocalization
import com.exura.features.plandefinition.presentation.PlanDefinitionListSelectionDialogContent
import com.exura.features.plandefinition.presentation.PlanStatisticElement
import com.exura.features.publicplans.domain.model.ProviderPublicPlan
import com.exura.features.publicplans.domain.model.PublicPlanStatistics
import com.exura.features.publicplans.publicPlansLocalization
import com.exura.ui.Dimens
import com.exura.navigation.Route
import com.exura.pages.publicplans.widgets.PublicPlanSharingEnabledWidget
import com.exura.ui.Assets
import com.exura.util.formatLocalDate
import com.varabyte.kobweb.compose.css.AspectRatio
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.aspectRatio
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.datetime.LocalDateTime

@Page(Route.PUBLIC_PLAN)
@Layout(".components.layouts.NavigableLayout")
@Composable
fun NavigableLayoutScope.PublicPlanListPage() {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()

    val controller = rememberPublicPlanListController(scope, context)
    val state = controller.uiState

    val onPlanCreateClicked = {
        showDialog(DialogPanelLargeVariant) {
            PlanDefinitionListSelectionDialogContent(
                onPlanSelected = { versionedPlan ->
                    versionedPlan.firebaseDocId?.let { selectedPlanId ->
                        controller.onPlanSelectedForPublish(selectedPlanId)
                    }
                }
            )
        }
    }

    Column(
        modifier = Modifier.fillMaxSize()
    ) {
        PageNavigationHeaderSection(
            publicPlansLocalization.current.publicPlans,
            isPageWorking = state.isLoading,
            onBack = null,
        )

        when {
            state.isLoading && state.plans.isEmpty() -> Loader()
            state.error != null -> PageErrorContent(errorMessage = state.error)
            else -> PublicPlansListView(
                modifier = Modifier.weight(1f),
                plans = state.plans,
                onPlanClicked = controller::onPlanClicked,
                onRefresh = controller::loadPlans,
                onCreateClicked = onPlanCreateClicked,
            )
        }
    }
}


@Composable
private fun PublicPlansListView(
    modifier: Modifier = Modifier,
    plans: List<ProviderPublicPlan>,
    onPlanClicked: (String) -> Unit,
    onRefresh: () -> Unit,
    onCreateClicked: () -> Unit
) = PageMainContent(modifier) {
    Column(modifier = Modifier.fillMaxSize()) {
        PublicPlansListOptionsSection(
            onRefresh = onRefresh,
            onCreateClicked = onCreateClicked,
        )
        PublicPlansList(
            modifier = Modifier.weight(1f),
            plans = plans,
            onPlanClicked = onPlanClicked,
        )
    }
}

@Composable
private fun PublicPlansListOptionsSection(
    onRefresh: () -> Unit,
    onCreateClicked: () -> Unit,
) = PageOptionsSection {
    AsyncButton(
        text = publicPlansLocalization.current.addPublicPlan,
        onClick = onCreateClicked,
        icon = { UntiledIcon(Assets.IconsUntiled.GENERAL_ADD) },
        variant = ButtonOutlineVariant,
    )
    AsyncButton(
        text = commonLocalization.current.controls.refresh,
        onClick = onRefresh,
        icon = { UntiledIcon(Assets.IconsUntiled.GENERAL_REFRESH) },
        variant = ButtonOutlineVariant,
    )
}

@Composable
private fun PublicPlansList(
    modifier: Modifier = Modifier,
    plans: List<ProviderPublicPlan>,
    onPlanClicked: (String) -> Unit,
) = PageSecondaryContent(modifier) {
    Column(modifier = Modifier.fillMaxSize()) {
        plans.forEach { plan ->
            PublicPlanEntry(
                plan = plan,
                onClick = { plan.id?.let { onPlanClicked(plan.id) } },
            )
        }
    }
}

@Composable
private fun PublicPlanEntry(
    plan: ProviderPublicPlan,
    onClick: () -> Unit,
) = GeneralListEntry(
    modifier = Modifier.onClick { onClick() },
    variant = HoverableListEntryVariant,
) {
    PublicPlanEntrySharingDetails(
        accessCode = plan.accessCode,
        isSharingEnabled = plan.isSharingEnabled,
        publishedDate = plan.createdDate,
    )

    CurrentPublicPlanDetails(
        modifier = Modifier.weight(1f),
        title = plan.summary.title,
        sharingId = plan.id!!,
        modificationDate = plan.lastModifiedDate,
        daysCount = plan.summary.planDuration,
        commCount = (plan.summary.numberFloating + plan.summary.numberQuestion + plan.summary.numberPersistent + plan.summary.numberInstruction),
        statistics = plan.statistics,
    )

    Spacer()

    plan.summary.imageUrl?.let { url ->
        if (url.isNotEmpty()) {
            val colorMode = ColorMode.current
            val backgroundMod = if (colorMode.isDark) Modifier.backgroundColor(colorMode.toPalette().color)
            else Modifier
            Box(
                modifier = backgroundMod.size(Dimens.listEntryImageSize)
                    .borderRadius(Dimens.listEntryBackgroundBorderRadius)
            ) {
                Image(src = url, Modifier.aspectRatio(AspectRatio.Auto).fillMaxSize())
            }
        }
    }
}

@Composable
private fun PublicPlanEntrySharingDetails(
    modifier: Modifier = Modifier,
    accessCode: String,
    isSharingEnabled: Boolean,
    publishedDate: LocalDateTime,
) {
    Column(
        modifier = Modifier.then(modifier),
        verticalArrangement = Arrangement.spacedBy(Dimens.listEntryTopDownSpacer),
    ) {
        PublicPlanSharingEnabledWidget(
            accessCode = accessCode,
            isSharingEnabled = isSharingEnabled,
        )
        UnderwrittenText(
            underwriteVariant = ExuraCommentaryTextVariant,
            underwrite = { SpanText(publicPlansLocalization.current.planSharing.firstPublish) },
        ) {
            InlineIcon(icon = { UntiledIcon(Assets.IconsUntiled.GENERAL_DATE) }) {
                SpanText(formatLocalDate(publishedDate))
            }
        }
    }
}

@Composable
private fun CurrentPublicPlanDetails(
    modifier: Modifier = Modifier,
    title: String,
    sharingId: String,
    modificationDate: LocalDateTime,
    daysCount: Int,
    commCount: Int,
    statistics: PublicPlanStatistics,
) {
    Column(
        modifier = Modifier.then(modifier),
        verticalArrangement = Arrangement.spacedBy(Dimens.listEntryTopDownSpacer),
    ) {
        UnderwrittenText(
            underwriteVariant = ExuraCommentaryTextVariant,
            underwrite = { SpanText("ID: $sharingId") },
        ) {
            SpanText(title)
        }

        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Dimens.listEntryLeftRightSpacer)
        ) {
            PlanStatisticElement(
                underwriteText = planLocalization.current.metadata.duration,
                iconSrc = Assets.IconsUntiled.DURATION,
            ) { SpanText(planLocalization.current.daysCount(daysCount)) }

            PlanStatisticElement(
                underwriteText = planLocalization.current.series.communiques(commCount),
                iconSrc = Assets.IconsUntiled.COMMUNICATES_ALL,
            ) { SpanText(commCount.toString()) }

            PlanStatisticElement(
                underwriteText = publicPlansLocalization.current.planStatistics.views(statistics.viewCount),
                iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_VIEWS,
            ) { SpanText(statistics.viewCount.toString()) }

            PlanStatisticElement(
                underwriteText = publicPlansLocalization.current.planStatistics.accesses(statistics.accessCount),
                iconSrc = Assets.IconsUntiled.PUBLIC_PLAN_ACCESSES,
            ) { SpanText(statistics.accessCount.toString()) }

            PlanStatisticElement(
                underwriteText = publicPlansLocalization.current.planStatistics.lastModification,
                iconSrc = Assets.IconsUntiled.GENERAL_DATE,
            ) {
                SpanText(
                    formatLocalDate(modificationDate, isWithHours = true),
                    modifier = ExuraTextStyle.toModifier(ExuraBaseWeightVariant),
                )
            }
        }
    }
}
